const kEmailRE =
  /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

/**
 * Validates value agains schema.
 */
class Schema {
  constructor(model) {
    this.#model = model;
    if (!(model instanceof Function)) {
      for (const [prop, func] of Object.entries(model)) {
        this[prop] = this.mapper.bind(null, prop, func);
      }
    }
  }

  static Any(v) {
    return v;
  }

  static AnyObject(v) {
    if (!v || typeof v != 'object') {
      throw new Error(`Bad object: ${Schema.stringify(v)}`);
    }
    return v;
  }

  static Boolean(v) {
    if (typeof v != 'boolean') {
      throw new Error(`Not a boolean: ${Schema.stringify(v)}`);
    }
    return v;
  }

  static Date(v) {
    let vv = v;

    // Do not use Date.parse because it is unpredictable and converts
    // a string into a date of a local timezone.
    if (typeof v == 'string') {
      let match =
        v.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}):(\d{2}))?/) ?? [];
      let [, year, month, day, hours = '0', minutes = '0', seconds = '0'] =
        match;
      if (year == undefined || month == undefined || day == undefined) {
        throw new Error(`Invalid date format: ${Schema.stringify(v)}`);
      }
      vv = Date.UTC(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hours),
        parseInt(minutes),
        parseInt(seconds)
      );
    }

    let date = new Date(vv);
    if (date.valueOf() === 0 || isNaN(date)) {
      throw new Error(`Invalid date: ${Schema.stringify(v)}`);
    }
    return date;
  }

  static Int(v) {
    let int = Number.parseInt(v);
    if (Number.isNaN(int)) {
      throw new Error(`NaN value: ${Schema.stringify(v)}`);
    }
    return int;
  }

  static UnsignedInt(v) {
    let int = Schema.Int(v);
    if (int < 0) {
      throw new Error(`Wrong unsinged int: ${Schema.stringify(v)}`);
    }
    return int;
  }

  static Float(v) {
    let float = Number.parseFloat(v);
    if (Number.isNaN(float)) {
      throw new Error(`NaN value: ${Schema.stringify(v)}`);
    }
    return float;
  }

  static Latitude(v) {
    let dd = Schema.Float(v);
    if (dd < -90 || dd > 90) {
      throw new Error(`Wrong latitude: ${Schema.stringify(v)}`);
    }
    return dd;
  }

  static Longitude(v) {
    let dd = Schema.Float(v);
    if (dd < -180 || dd > 180) {
      throw new Error(`Wrong longitude: ${Schema.stringify(v)}`);
    }
    return dd;
  }

  static Percent(v) {
    if (!Number.isSafeInteger(v) || v < 0 || v > 100) {
      throw new Error(`Wrong percent value: ${Schema.stringify(v)}`);
    }
    return v;
  }

  static Probability(v) {
    if (!Number.isFinite(v) || v < 0 || v > 1) {
      throw new Error(`Wrong probability value: ${Schema.stringify(v)}`);
    }
    return v;
  }

  static StrictString(v) {
    if (typeof v != 'string') {
      throw new Error(`Invalid string: ${Schema.stringify(v)}`);
    }
    return v;
  }

  static String(v) {
    if (v === undefined || v === null) {
      return '';
    }
    return Schema.StrictString(v);
  }

  static NotEmptyString(v) {
    if (!v) {
      throw new Error('Empty value');
    }
    return Schema.StrictString(v);
  }

  static FixedString(length) {
    return v => {
      if (!v || v.length != length) {
        throw new Error(
          `Invalid ${length} chars long string: ${Schema.stringify(v)}`
        );
      }
      return v;
    };
  }

  static Email(v) {
    if (!kEmailRE.test(v)) {
      throw new Error(`Not email, got: ${(() => Schema.stringify(v))()}`);
    }
    return v;
  }

  static Enum(...items) {
    return v => {
      if (![...items].includes(v)) {
        throw new Error(
          `Illegal enum value: ${Schema.stringify(v)}, allowed values: ${[
            ...items,
          ]
            .map(i => `'${i}'`)
            .join(', ')}`
        );
      }
      return v;
    };
  }

  static Set(...items) {
    return function Set(v) {
      if (!(v instanceof Set) && !(v instanceof Array)) {
        throw new Error(
          `Set value must be of Set or Array type, got: ${typeof v}, value: ${Schema.stringify(
            v
          )}`
        );
      }
      for (let vitem of v) {
        if (![...items].includes(vitem)) {
          throw new Error(
            `Illegal set item: ${Schema.stringify(vitem)}, allowed values: ${[
              ...items,
            ]
              .map(i => `'${i}'`)
              .join(', ')}`
          );
        }
      }
      return v;
    };
  }

  // Validates an array consisting of the given list of elements.
  static List(...models) {
    return v => {
      if (!(v instanceof Array)) {
        throw new Error(`Not array: ${Schema.stringify(v)}`);
      }

      const all_models = [...models];

      let v_copy = [];
      for (let i = 0; i < all_models.length; i++) {
        v_copy.push(all_models[i](v[i]));
      }
      return v_copy;
    };
  }

  // Validates an array containing elements corresponding to the given model.
  static Array(model) {
    return v => {
      if (!(v instanceof Array)) {
        throw new Error(`Not array: ${Schema.stringify(v)}`);
      }

      if (model instanceof Function) {
        return v.map(i => model(i));
      }

      let v_copy = [];
      for (let prop in model) {
        v_copy.push(model[prop](v[prop]));
      }
      return v_copy;
    };
  }

  static Object(model) {
    return v => {
      if (typeof v != 'object') {
        throw new Error(`Not object: '${v}'`);
      }
      let v_copy = {};
      for (let key in model) {
        try {
          let value = model[key](v[key]);
          if (key in v) {
            v_copy[key] = value;
          }
        } catch (e) {
          if (!(key in v)) {
            throw new Error(
              `Object doesn't have '${key}' key: '${Schema.stringify(v)}'`
            );
          }
          throw e;
        }
      }
      return v_copy;
    };
  }

  /**
   * Validates a map object: { key: value }. The model describes map values.
   */
  static Map(model) {
    return v => {
      if (typeof v != 'object') {
        throw new Error(`Not object: '${Schema.stringify(v)}'`);
      }
      let v_copy = {};
      for (let key in v) {
        try {
          v_copy[key] = model(v[key]);
        } catch (e) {
          throw new Error(`Object '${key}' key has thrown '${e.message}'`);
        }
      }
      return v_copy;
    };
  }

  static Default(model, default_value) {
    return v => {
      if (v === default_value || v === undefined) {
        return default_value;
      }
      return model(v);
    };
  }

  static Optional(model) {
    return v => {
      if (v === undefined) {
        return v;
      }
      return model(v);
    };
  }

  static OptionalInt(v) {
    return Schema.Optional(Schema.Int)(v);
  }

  static OptionalUnsignedInt(v) {
    return Schema.Optional(Schema.UnsignedInt)(v);
  }

  static OptionalFloat(v) {
    return Schema.Optional(Schema.Float)(v);
  }

  static OptionalLatitude(v) {
    return Schema.Optional(Schema.Latitude)(v);
  }

  static OptionalLongitude(v) {
    return Schema.Optional(Schema.Longitude)(v);
  }

  static OptionalProbability(v) {
    return Schema.Optional(Schema.Probability)(v);
  }

  static OptionalDate(v) {
    return Schema.Optional(Schema.Date)(v);
  }

  static OptionalString(v) {
    return Schema.Optional(Schema.String)(v);
  }

  static OptionalEnum(...args) {
    return Schema.Optional(Schema.Enum(...args));
  }

  static OptionalArray(model) {
    return Schema.Optional(Schema.Array(model));
  }

  static OptionalObject(model) {
    return Schema.Optional(Schema.Object(model));
  }

  static Or(...conditions) {
    return v => {
      let errors = [];
      for (let condition of [...conditions]) {
        try {
          if (condition instanceof Function) {
            return condition(v);
          }
          if (condition !== v) {
            throw new Error(
              `Not equal: got: ${Schema.stringify(
                v
              )}, expected: ${Schema.stringify(condition)}`
            );
          }
          return v;
        } catch (e) {
          errors.push(e.message);
          // continue
        }
      }
      throw new Error(
        `${Schema.stringify(
          v
        )} doesn't meet any criteria, criteria errors: ${errors
          .map(e => `"${e}"`)
          .join(', ')}}`
      );
    };
  }

  /**
   * Returns true if the given value matches the given model.
   */
  static is(model, value) {
    try {
      model(value);
      return true;
    } catch (e) {
      return false;
    }
  }

  get model() {
    return this.#model;
  }

  /**
   * Validates the given data against the schema.
   */
  validate(data) {
    if (this.#model instanceof Function) {
      return this.#model(data);
    }

    let validatedData = {};
    for (let prop in this) {
      try {
        let value = this[prop](data[prop]);
        if (prop in data) {
          validatedData[prop] = value;
        }
      } catch (e) {
        if (!(prop in data)) {
          throw new Error(
            `Property '${prop}' is missing in ${Schema.stringify(data)}`
          );
        }
        throw e;
      }
    }
    return validatedData;
  }

  mapper(prop, func, v) {
    try {
      return func(v);
    } catch (e) {
      throw new Error(`Bad '${prop}' value is given. ${e.message}`);
    }
  }

  static stringify(v) {
    try {
      return JSON.stringify(v);
    } catch (e) {
      console.error(`Failed to convert object to JSON`);
      console.error(v);
      console.error(e);
      return `${v}`;
    }
  }

  #model;
}

/**
 * Schema to validate data by one of the models.
 **/
class OrSchema extends Schema {
  constructor(...models) {
    let schema_models = [...models].map(model => new Schema(model));
    super(Schema.Or(...schema_models.map(schema => v => schema.validate(v))));
  }
}

export { Schema, OrSchema };
